import React, { useState, useEffect } from 'react';
import { Plus, CreditCard as Edit, Trash2, CreditCard, User, Calendar, CheckCircle, Clock, Download, ThumbsUp, ThumbsDown, AlertCircle, FileDown } from 'lucide-react';
import { Payment } from '../../types/collaborators';
import { paymentsService } from '../../services/supabaseService';
import { notificationService } from '../../services/notificationService';
import PaymentModal from './PaymentModal';
import ExportInvoicesModal from './ExportInvoicesModal';
import { usePermissions } from '../../hooks/usePermissions';
import { formatCurrency } from '../../utils/textUtils';

const PaymentsList: React.FC = () => {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'scheduled' | 'executed'>('all');
  const [approvalFilter, setApprovalFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectingPaymentId, setRejectingPaymentId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  const { isAdmin, isCollaboradorProgramador } = usePermissions();
  const isProgrammerView = isCollaboradorProgramador();

  useEffect(() => {
    loadPayments();
  }, []);

  const loadPayments = async () => {
    try {
      setLoading(true);
      console.log('🔄 Cargando pagos desde Supabase...');
      const paymentsData = await paymentsService.getAll();
      setPayments(paymentsData);
      console.log('✅ Pagos cargados:', paymentsData.length);
    } catch (error) {
      console.error('Error loading payments:', error);
      alert('Error al cargar los pagos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePayment = () => {
    setEditingPayment(null);
    setIsModalOpen(true);
  };

  const handleEditPayment = (payment: Payment) => {
    setEditingPayment(payment);
    setIsModalOpen(true);
  };

  const handleSavePayment = (paymentData: Partial<Payment>) => {
    if (editingPayment) {
      // Update existing payment
      updatePayment(paymentData);
    } else {
      // Create new payment
      createPayment(paymentData);
    }
  };

  const createPayment = async (paymentData: Partial<Payment>) => {
    try {
      console.log('🔄 Creando pago en Supabase...');
      await paymentsService.create({
        invoiceId: paymentData.invoiceId,
        collaboratorId: paymentData.collaboratorId,
        paymentDate: paymentData.paymentDate,
        amount: paymentData.amount,
        currency: paymentData.currency,
        paymentMethod: paymentData.paymentMethod,
        proofUrl: paymentData.proofUrl,
        proofName: paymentData.proofName,
        status: paymentData.status || 'scheduled',
        notes: paymentData.notes,
        executedBy: paymentData.executedBy
      });
      
      await loadPayments();
      setIsModalOpen(false);
      setEditingPayment(null);
      alert('✅ Pago registrado correctamente');

      await notificationService.createNotification({
        code: 'PAYMENT.CREATED',
        title: 'Pago Registrado',
        body: `Nuevo pago de ${paymentData.amount}€ programado`,
        severity: 'info',
        data: { payment_id: newPayment.id, amount: paymentData.amount }
      });
    } catch (error) {
      console.error('Error creating payment:', error);
      alert('❌ Error al registrar el pago');
    }
  };

  const updatePayment = async (paymentData: Partial<Payment>) => {
    if (!editingPayment) return;
    
    try {
      console.log('🔄 Actualizando pago en Supabase...');
      await paymentsService.update(editingPayment.id, {
        invoiceId: paymentData.invoiceId,
        collaboratorId: paymentData.collaboratorId,
        paymentDate: paymentData.paymentDate,
        amount: paymentData.amount,
        currency: paymentData.currency,
        paymentMethod: paymentData.paymentMethod,
        proofUrl: paymentData.proofUrl,
        proofName: paymentData.proofName,
        status: paymentData.status,
        notes: paymentData.notes,
        executedBy: paymentData.executedBy
      });
      
      await loadPayments();
      setIsModalOpen(false);
      setEditingPayment(null);
      alert('✅ Pago actualizado correctamente');

      await notificationService.createNotification({
        code: 'PAYMENT.UPDATED',
        title: 'Pago Actualizado',
        body: `Se actualizó el pago de ${paymentData.amount}€`,
        severity: 'info',
        data: { payment_id: editingPayment.id, amount: paymentData.amount }
      });
    } catch (error) {
      console.error('Error updating payment:', error);
      alert('❌ Error al actualizar el pago');
    }
  };

  const handleApprovePayment = async (paymentId: string) => {
    if (!window.confirm('¿Aprobar este pago? Una vez aprobado, podrá ser ejecutado.')) {
      return;
    }

    try {
      console.log('🔄 Aprobando pago:', paymentId);
      await paymentsService.approve(paymentId, 'Usuario Actual');
      await loadPayments();
      alert('✅ Pago aprobado correctamente');

      const payment = payments.find(p => p.id === paymentId);
      await notificationService.createNotification({
        code: 'PAYMENT.APPROVED',
        title: 'Pago Aprobado',
        body: `El pago de ${payment?.amount}€ ha sido aprobado`,
        severity: 'info',
        data: { payment_id: paymentId }
      });
    } catch (error) {
      console.error('Error approving payment:', error);
      alert('❌ Error al aprobar el pago: ' + (error as Error).message);
    }
  };

  const handleRejectPayment = (paymentId: string) => {
    setRejectingPaymentId(paymentId);
    setRejectModalOpen(true);
  };

  const handleConfirmReject = async () => {
    if (!rejectingPaymentId || !rejectionReason.trim()) {
      alert('Por favor ingresa un motivo de rechazo');
      return;
    }

    try {
      console.log('🔄 Rechazando pago:', rejectingPaymentId);
      await paymentsService.reject(rejectingPaymentId, 'Usuario Actual', rejectionReason);
      await loadPayments();
      setRejectModalOpen(false);
      setRejectingPaymentId(null);
      setRejectionReason('');
      alert('✅ Pago rechazado correctamente');

      const payment = payments.find(p => p.id === rejectingPaymentId);
      await notificationService.createNotification({
        code: 'PAYMENT.REJECTED',
        title: 'Pago Rechazado',
        body: `El pago de ${payment?.amount}€ ha sido rechazado`,
        severity: 'warn',
        data: { payment_id: rejectingPaymentId, reason: rejectionReason }
      });
    } catch (error) {
      console.error('Error rejecting payment:', error);
      alert('❌ Error al rechazar el pago: ' + (error as Error).message);
    }
  };

  const handleExecutePayment = async (paymentId: string) => {
    const payment = payments.find(p => p.id === paymentId);
    if (!payment) return;

    if (payment.approvalStatus !== 'approved') {
      alert('❌ El pago debe estar aprobado antes de ser ejecutado');
      return;
    }

    if (!window.confirm('¿Marcar este pago como ejecutado? Esta acción actualizará el estado de la factura a "pagada".')) {
      return;
    }

    try {
      console.log('🔄 Ejecutando pago:', paymentId);
      await paymentsService.execute(paymentId, 'Usuario Actual');
      await loadPayments();
      alert('✅ Pago ejecutado correctamente y factura actualizada');

      const paymentData = payments.find(p => p.id === paymentId);

      // Notif targeteada al colaborador — antes iba broadcast (nadie lo veía).
      await notificationService.createNotification({
        user_id: (paymentData as any)?.userId || (paymentData as any)?.collaboratorId,
        code: 'PAYMENT.EXECUTED',
        title: 'Tu pago se ha ejecutado',
        body: `${paymentData?.amount}€ · ${paymentData?.currency || 'EUR'}`,
        severity: 'info',
        data: {
          payment_id: paymentId,
          invoice_id: (paymentData as any)?.invoiceId,
          amount: paymentData?.amount,
        },
      });

      // Email al colaborador con el justificante si existe.
      try {
        const { supabase } = await import('../../services/supabaseService');
        const userId = (paymentData as any)?.userId || (paymentData as any)?.collaboratorId;
        if (userId) {
          const { data: user } = await supabase
            .from('users')
            .select('name, email')
            .eq('id', userId)
            .maybeSingle();
          if (user?.email) {
            const { emailNotificationService } = await import('../../services/emailNotificationService');
            const invNum = (paymentData as any)?.invoiceNumber || (paymentData as any)?.invoice_number || '';
            await emailNotificationService.sendInvoicePaidEmail(
              user.name || 'Colaborador',
              user.email,
              userId,
              invNum,
              paymentData?.amount || 0,
              (paymentData?.currency as any) || 'EUR',
              new Date(),
              'Transferencia',
              (paymentData as any)?.invoiceId || ''
            );
          }
        }
      } catch (emailErr) {
        console.error('Error email pago ejecutado:', emailErr);
      }
    } catch (error) {
      console.error('Error executing payment:', error);
      alert('❌ Error al ejecutar el pago: ' + (error as Error).message);
    }
  };

  const handleDeletePayment = async (paymentId: string) => {
    const payment = payments.find(p => p.id === paymentId);
    if (payment && window.confirm(`¿Eliminar el pago para "${payment.collaboratorName}"? Esta acción no se puede deshacer.`)) {
      try {
        console.log('🔄 Eliminando pago:', paymentId);
        await paymentsService.delete(paymentId);
        await loadPayments();
        alert('✅ Pago eliminado correctamente');

        await notificationService.createNotification({
          code: 'PAYMENT.DELETED',
          title: 'Pago Eliminado',
          body: `Se eliminó el pago de ${payment.amount}€`,
          severity: 'warn',
          data: { amount: payment.amount }
        });
      } catch (error) {
        console.error('Error deleting payment:', error);
        alert('❌ Error al eliminar el pago');
      }
    }
  };

  const getStatusColor = (status: Payment['status']) => {
    switch (status) {
      case 'scheduled': return 'bg-yellow-100 text-yellow-800';
      case 'executed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: Payment['status']) => {
    switch (status) {
      case 'scheduled': return 'Programado';
      case 'executed': return 'Ejecutado';
      default: return status;
    }
  };

  const getApprovalStatusColor = (status: Payment['approvalStatus']) => {
    switch (status) {
      case 'pending': return 'bg-orange-100 text-orange-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getApprovalStatusLabel = (status: Payment['approvalStatus']) => {
    switch (status) {
      case 'pending': return 'Pendiente';
      case 'approved': return 'Aprobado';
      case 'rejected': return 'Rechazado';
      default: return status;
    }
  };

  const filteredPayments = payments.filter(p => {
    const statusMatch = filter === 'all' || p.status === filter;
    const approvalMatch = approvalFilter === 'all' || p.approvalStatus === approvalFilter;
    return statusMatch && approvalMatch;
  });

  const totalAmount = filteredPayments.reduce((sum, p) => sum + p.amount, 0);
  const executedAmount = payments.filter(p => p.status === 'executed').reduce((sum, p) => sum + p.amount, 0);
  const scheduledAmount = payments.filter(p => p.status === 'scheduled').reduce((sum, p) => sum + p.amount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3">Cargando pagos...</span>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Pagos a Plantilla</h2>
          <p className="text-gray-600">
            {isProgrammerView ? 'Registro de tus pagos recibidos' : 'Registro y seguimiento de pagos realizados'}
          </p>
        </div>
        {!isProgrammerView && (
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setIsExportModalOpen(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <FileDown className="w-4 h-4" />
              <span>Exportar Facturas</span>
            </button>
            <button
              onClick={handleCreatePayment}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Registrar Pago</span>
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      {!isProgrammerView && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6 space-y-3">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-gray-700">Estado de pago:</span>
            <div className="flex space-x-2">
              {[
                { value: 'all', label: 'Todos' },
                { value: 'scheduled', label: 'Programados' },
                { value: 'executed', label: 'Ejecutados' }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => setFilter(option.value as any)}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    filter === option.value
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-gray-700">Estado de aprobación:</span>
            <div className="flex space-x-2">
              {[
                { value: 'all', label: 'Todos' },
                { value: 'pending', label: 'Pendientes' },
                { value: 'approved', label: 'Aprobados' },
                { value: 'rejected', label: 'Rechazados' }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => setApprovalFilter(option.value as any)}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                    approvalFilter === option.value
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600">Total Pagos</p>
              <p className="text-2xl font-bold text-blue-900">{filteredPayments.length}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <CreditCard className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600">Pagos Ejecutados</p>
              <p className="text-2xl font-bold text-green-900">{executedAmount.toLocaleString()}€</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-yellow-600">Pagos Programados</p>
              <p className="text-2xl font-bold text-yellow-900">{scheduledAmount.toLocaleString()}€</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-purple-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-purple-600">Importe Total</p>
              <p className="text-2xl font-bold text-purple-900">{totalAmount.toLocaleString()}€</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <CreditCard className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Payments Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pago
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Colaborador
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha de Pago
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Importe
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Método
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Aprobación
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Comprobante
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPayments.map((payment) => (
                <tr key={payment.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <CreditCard className="w-4 h-4 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          Pago #{payment.id}
                        </div>
                        <div className="text-xs text-gray-500">
                          Factura: {payment.invoiceNumber}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-900">{payment.collaboratorName}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-900">
                        {payment.paymentDate.toLocaleDateString()}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-900">
                      {formatCurrency(payment.amount, payment.currency)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-900">{payment.paymentMethod}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(payment.status)}`}>
                      {getStatusLabel(payment.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getApprovalStatusColor(payment.approvalStatus)}`}>
                        {getApprovalStatusLabel(payment.approvalStatus)}
                      </span>
                      {payment.approvalStatus === 'rejected' && payment.rejectionReason && (
                        <div className="flex items-start space-x-1 text-xs text-red-600">
                          <AlertCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          <span className="line-clamp-2">{payment.rejectionReason}</span>
                        </div>
                      )}
                      {payment.approvedBy && (
                        <div className="text-xs text-gray-500">
                          por {payment.approvedBy}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {payment.proofUrl ? (
                      <button
                        onClick={async () => {
                          const { supabase } = await import('../../services/supabaseService');
                          const { data } = await supabase.storage
                            .from('payment-proofs')
                            .createSignedUrl(payment.proofUrl!, 60 * 60);
                          if (data?.signedUrl) window.open(data.signedUrl, '_blank');
                        }}
                        className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-sm"
                      >
                        <Download className="w-3 h-3" />
                        <span>{payment.proofName || 'Ver'}</span>
                      </button>
                    ) : (
                      <span className="text-sm text-gray-400">Sin justificante</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      {payment.approvalStatus === 'pending' && (
                        <>
                          <button
                            onClick={() => handleApprovePayment(payment.id)}
                            className="text-green-600 hover:text-green-900 p-1 rounded"
                            title="Aprobar pago"
                          >
                            <ThumbsUp className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleRejectPayment(payment.id)}
                            className="text-red-600 hover:text-red-900 p-1 rounded"
                            title="Rechazar pago"
                          >
                            <ThumbsDown className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {payment.status === 'scheduled' && payment.approvalStatus === 'approved' && (
                        <button
                          onClick={() => handleExecutePayment(payment.id)}
                          className="text-green-600 hover:text-green-900 p-1 rounded"
                          title="Marcar como ejecutado"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      )}
                      {payment.status !== 'executed' && (
                        <button
                          onClick={() => handleEditPayment(payment)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded"
                          title="Editar pago"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      )}
                      {payment.status !== 'executed' && (
                        <button
                          onClick={() => handleDeletePayment(payment.id)}
                          className="text-red-600 hover:text-red-900 p-1 rounded"
                          title="Eliminar pago"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredPayments.length === 0 && (
          <div className="text-center py-12">
            <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay pagos</h3>
            <p className="text-gray-500 mb-6">
              {filter === 'all' 
                ? 'Comienza registrando el primer pago'
                : `No hay pagos con estado "${getStatusLabel(filter as any)}"`
              }
            </p>
            {filter === 'all' && (
              <button
                onClick={handleCreatePayment}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors mx-auto"
              >
                <Plus className="w-4 h-4" />
                <span>Registrar Pago</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingPayment(null);
        }}
        onSave={handleSavePayment}
        payment={editingPayment}
      />

      {/* Reject Modal */}
      {rejectModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Rechazar Pago</h2>
              <button
                onClick={() => {
                  setRejectModalOpen(false);
                  setRejectingPaymentId(null);
                  setRejectionReason('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <ThumbsDown className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-600 mb-4">
                Por favor, proporciona una razón para el rechazo de este pago. Esta información será visible para el equipo.
              </p>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Ej: El monto no coincide con la factura, falta documentación de respaldo..."
                required
              />
            </div>
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setRejectModalOpen(false);
                  setRejectingPaymentId(null);
                  setRejectionReason('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmReject}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Rechazar Pago
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      <ExportInvoicesModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
      />
    </div>
  );
};

export default PaymentsList;